import { Archive, Percent, Wallet } from '@phosphor-icons/react';

import { formatSourceTypicalAmount } from '../../lib/incomeSourceBuckets';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { RowActions } from '../common/RowActions';
import { IncomeSourceIcon } from './IncomeSourceIcon';

function categoryLabel(source) {
  return pt.income.sources.categories[source.category] || source.category;
}

/**
 * Linha de fonte no balde — `.pen` `ds/card/income-source-row` (`CkK4o`),
 * estendido com `sub` (categoria), `note` (nota opcional) e `actions`
 * (editar/arquivar inline) para o contexto de gestão de fontes.
 *
 * O badge à direita (à mão / bruto / arquivada) fica sob o valor — não junto
 * ao nome. Sem barra de progresso: essa faixa (`% do ciclo`) é só para a
 * listagem de registos do ciclo, não para esta página de gestão.
 */
export function IncomeSourceRow({
  source,
  onEdit,
  onArchive,
  onRestore,
  isFirst = false,
  className,
  style,
  ...rest
}) {
  const amount = formatSourceTypicalAmount(source);
  const isArchived = source.is_active === false;
  const isGross = source.is_net === false;
  const category = categoryLabel(source);

  return (
    <div
      className={cn(
        'group flex min-w-0 flex-col gap-space-2 py-space-3',
        !isFirst && 'border-t border-border',
        isArchived && 'opacity-90',
        className,
      )}
      style={style}
      {...rest}
    >
      <div className="flex min-w-0 items-center gap-space-2">
        <IncomeSourceIcon
          source={source}
          background="muted"
          className="h-7 w-7 rounded-ds-sm border border-border"
          iconClassName="h-4 w-4"
        />

        <button
          type="button"
          className="flex min-w-0 flex-1 flex-col gap-0.5 text-left"
          onClick={() => onEdit?.(source)}
        >
          <span className="type-display-row truncate text-card-foreground">{source.name}</span>
          {category ? (
            <span className="type-label-meta truncate text-muted-foreground">{category}</span>
          ) : null}
          {source.notes ? (
            <span className="type-chip truncate text-muted-foreground">{source.notes}</span>
          ) : null}
        </button>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <p className="type-display-row tnum text-card-foreground">{amount.label}</p>
          {isArchived ? (
            <span className="type-chip inline-flex items-center gap-1 rounded-ds-full border border-border bg-muted px-space-2 py-0.5 font-semibold text-muted-foreground">
              <Archive className="h-[11px] w-[11px]" aria-hidden />
              {pt.income.sources.archivedLabel}
            </span>
          ) : (
            <span
              className={cn(
                'type-chip inline-flex items-center gap-1 rounded-ds-full border px-space-2 py-0.5 font-semibold',
                isGross
                  ? 'border-warning-500 bg-warning-tint text-warning-fg'
                  : 'border-sage-border bg-sage-tint text-sage-on-tint',
              )}
            >
              {isGross ? (
                <Percent className="h-[11px] w-[11px]" aria-hidden />
              ) : (
                <Wallet className="h-[11px] w-[11px]" aria-hidden />
              )}
              {isGross ? pt.income.sources.grossBadge : pt.income.sources.netBadge}
            </span>
          )}
        </div>

        {onEdit || onArchive || onRestore ? (
          <div className="flex shrink-0 items-center gap-space-1">
            <RowActions
              item={source}
              onEdit={onEdit}
              onArchive={isArchived ? undefined : onArchive}
              onRestore={isArchived ? onRestore : undefined}
              editLabel={pt.income.sources.actions.edit}
              archiveLabel={pt.income.sources.actions.archive}
              restoreLabel={pt.income.sources.actions.unarchive}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}
